import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Subscription, Subject } from 'rxjs';

import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { fadeIn, fadeInDown } from '@shared/animations';
import { CartItem, CartItemsForm, CartItemFormItem } from '@pages/cart/models/cart.model';
import * as fromCart from '@app/pages/cart/store';
import { select, Store, ActionsSubject } from '@ngrx/store';
import * as fromStore from '@pages/cart/store';
import { Router } from '@angular/router';
import { AppState } from '@app/reducers';
import { environment } from '@environments/environment';
import { TranslationService } from '@core/service/translation.service';
import * as fromAuth from '@app/auth/store';
import { IUser, IUserClientReference } from '@app/auth/models/user';
import { NoteDeliverySelector } from '@app/pages/users/models/users.model';
import { debounceTime } from 'rxjs/operators';
import { CartHelperService } from '@app/pages/cart/services/carthelper.service';
import {
  buildCartItemUpdate,
  buildClientReferencesState,
  buildNoteDeliverySelectors,
  clearFormArray,
  collectUnsavedProductUpdates,
  getProductCleanState,
  getUserClientSelectorLabel,
  hasUnsavedCartProducts,
  normalizeCartQuantity,
  trackByCartItem,
} from '@app/pages/cart/utils/cart-list.util';

@Component({
  selector: 'app-cart-list',
  templateUrl: './cart-list.component.html',
  styleUrls: ['./cart-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeIn, fadeInDown]
})
export class CartListComponent implements OnInit, OnDestroy, OnChanges {

  /** Cart products */
  @Input() productsFrom: CartItem[] = [];

  /** Cart note update initalization */
  @Input() updateProductNotes: Subject<void>;

  @Input() preserveListSort: boolean;

  /**
   * Checked product list emitter.
   * @type {EventEmitter<any>}
   */
  @Output() readonly checkedList = new EventEmitter();
  @Output() readonly productsUpdated = new EventEmitter();
  @Output() readonly existNotUpdatedProductChange = new EventEmitter<boolean>();
  @Output() readonly productRemoved = new EventEmitter<boolean>();
  @Output() readonly setPreserveSort = new EventEmitter<boolean>();

  base_url = environment.api_url;

  products: CartItem[] = [];
  existNotUpdatedProduct = false;
  /**
   * Cart form group.
   * @type {FormGroupTyped<CartItemsForm>}
   */
  cartForm: FormGroupTyped<CartItemsForm>;
  cartFormProductsFA: FormArrayTyped<CartItemFormItem> = new FormArray([]);

  /** Checked products */
  checkedProducts: CartItemFormItem[] = [];

  productLoadTimeout: any;
  updateProductNotesSub: Subscription;
  cartValuesChangeSub: Subscription;
  user: IUser;

  noteDeliveryValues: NoteDeliverySelector[];
  noteDeliveryValuesCentral: NoteDeliverySelector[];

  readonly productsUpdatedEvent = new EventEmitter<CartItem[]>();
  readonly productsUpdatedEventObservable = this.productsUpdatedEvent.asObservable();
  productsUpdatedEventObservableSub: Subscription;
  updateProductsSuccessSub: Subscription;

  showUserClientRef = false;
  userClientRefValues: IUserClientReference[] = [];
  userClientRefObject: {[id: number]: IUserClientReference} = {};

  userSub: Subscription;
  removeStateSub: Subscription;
  productFormSubs: { [key: number]: Subscription } = {};

  showFifoStocks = false;
  updateProductsFifoSuccessSub: Subscription;
  showLocalPrice: boolean;
  cartHelperService: CartHelperService;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private fb: FormBuilder,
    private translationService: TranslationService,
    private store: Store<AppState>,
    private ref: ChangeDetectorRef,
    private actionsSubject: ActionsSubject,
  ) {
    this.cartHelperService = new CartHelperService(translationService, store, actionsSubject);
    this.cartHelperService.registerComponent(this, 'cart-list');
    this.initNDO(null);
  }


  ngOnInit(): void {
    this.userSub = this.store.pipe(select(fromAuth.getUser)).subscribe(user => {
      if (user && user.user_settings) {
        this.user = user;
      }
      if (user && user.user_settings && user.user_settings.show_local_currency) {
        this.showLocalPrice = user.user_settings.show_local_currency;
      }
      if (user && user.profile.user_field_values &&
          (user.profile.note_choice_on_add_cart || user.profile.company.note_choice_on_add_cart)) {
        this.initNDO(user);
      }
      if (user && (user.profile.company.hide_cards_in_stocks || user.user_settings.hide_cards_in_stocks)) {
        this.showFifoStocks = true;
      }
      this.initClientReferences(user);

      if (this.updateProductNotesSub) {
        this.updateProductNotesSub.unsubscribe();
      }
      this.updateProductNotesSub = this.updateProductNotes.subscribe(() => {
        this.saveAllProducts();
      });

      if (this.productsUpdatedEventObservableSub) {
        this.productsUpdatedEventObservableSub.unsubscribe();
      }
      this.productsUpdatedEventObservableSub = this.productsUpdatedEventObservable
        .pipe(debounceTime(200))
        .subscribe(productsFrom => {
          this.initProducts();
        });
      this.initForm();
      if (this.showFifoStocks) {
        if (this.updateProductsFifoSuccessSub) {
          this.updateProductsFifoSuccessSub.unsubscribe();
        }
        this.updateProductsFifoSuccessSub = this.cartHelperService.registerProductUpdateFifoProcessing();
      } else {
        // Called when products were updated
        if (this.updateProductsSuccessSub) {
          this.updateProductsSuccessSub.unsubscribe();
        }
        this.updateProductsSuccessSub = this.cartHelperService.registerProductUpdateProcessing();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productsFrom) {
      this.productsUpdatedEvent.emit(changes.productsFrom.currentValue);
    }
  }

  trackByFn(index, item: CartItem) {
    return trackByCartItem(index, item);
  }

  showUpdateSummary(previousValue: CartItem[], currentValue: CartItem[]) {
    // FIXME: for now we disable this notifications, as it does not work as expected.
    // const cartItem = this.cartForm.get('products').get(index.toString());
    // if (cartItem) {
    //   const quantity = cartItem.get<number>('quantity');
    //   const card = cartItem.get('card');

    //   this.products.map(product => {
    //     if (card.value === product.card) {
    //       // If cart returns smaller stock quantity.
    //       if (+quantity.value !== +product.quantity) {
    //         this.store.dispatch(
    //           new toast.ShowWarningToast(
    //             this.translationService.translate('PRODUCT_QUANTITY_NOT', {
    //               name: `${product.name} (${product.card})`
    //             })
    //           )
    //         );
    //         quantity.setValue(+product.quantity);
    //       }
    //     }
    //   });
    // }
  }

  initClientReferences(user) {
    const clientReferences = buildClientReferencesState(user);
    this.userClientRefValues = clientReferences.userClientRefValues;
    this.userClientRefObject = clientReferences.userClientRefObject;
    this.showUserClientRef = clientReferences.showUserClientRef;
    if (clientReferences.showUserClientRef) {
      this.ref.markForCheck();
    }
  }

  initNDO(loggedInUser: IUser) {
    const noteDeliverySelectors = buildNoteDeliverySelectors(
      key => this.translationService.translate(key),
      loggedInUser,
    );
    this.noteDeliveryValues = noteDeliverySelectors.noteDeliveryValues;
    this.noteDeliveryValuesCentral = noteDeliverySelectors.noteDeliveryValuesCentral;
  }
  initForm(): void {
    if (this.productLoadTimeout) {
      clearTimeout(this.productLoadTimeout);
    }
    this.productLoadTimeout = setTimeout(() => {
      this.cartForm = new FormGroup({
        products: this.cartFormProductsFA
      }) as FormGroupTyped<CartItemsForm>;
      this.initProducts();
      if (!this.cdRef['destroyed']) {
        this.cdRef.detectChanges();
      } else {
        // TODO: this is called... we need to check that this would never be called....
        // console.debug('actions on destrayed object');
      }
    });
  }

  initProducts(): void {
    this.setProducts(this.productsFrom);
    for (const key in this.productFormSubs) {
      if (this.productFormSubs.hasOwnProperty(key)) {
        if (this.productFormSubs[key]) {
          this.productFormSubs[key].unsubscribe();
        }
      }
    }
    const checkedIds = {};
    if (this.preserveListSort) {
      this.cartFormProductsFA.value.reduce((pv, cv, i) => {
        pv[+cv.id] = cv.checked;
        return pv;
      }, checkedIds);
    }

    this.removeProductFormGroups();
    this.products.map((product, index) => {
      this.cartFormProductsFA.push(
        this.fb.group({
          id: +product.id,
          name: product.name,
          card: product.card,
          weight: product.weight,
          height: product.height,
          dimensions: product.dimensions,
          picture_url: product.picture_url,
          skn: product.skn,
          product_id: product.product_id,
          manufacturer_name: product.manufacturer_name,
          sub_group_name: product.sub_group_name,
          // price: product.price,
          quantity: +product.quantity,
          note: [product.note, Validators.maxLength(26)],
          ndo: [product.ndo],
          client_ref_id: [product.client_ref_id],
          checked: this.preserveListSort ? checkedIds[+product.id] : false,
          warehouse: product.warehouse,
          is_central: product.is_central,
          on_the_way: product.on_the_way,
          is_carried: product.is_carried,
          reference_ids: [product.reference_ids],
        })
      );
    });
    if (!this.preserveListSort) {
      this.setPreserveSort.emit(true);
    }
    this.updateExistNotUpdatedProduct();
    this.updateCheckedProductsList();
    this.cartFormProductsFA.controls.map((control: AbstractControlTyped<CartItemFormItem>, index) => {
      if (this.productFormSubs[index]) {
        this.productFormSubs[index].unsubscribe();
      }
      this.productFormSubs[index] = control
        .valueChanges
        .pipe(debounceTime(100))
        .subscribe((productFormValue => {
          const cleanState = getProductCleanState(this.products[index], productFormValue);
          this.products[index].are_clean_fields = cleanState.are_clean_fields;
          this.products[index].are_clean_field_note = cleanState.are_clean_field_note;
          if (!cleanState.are_clean_fields) {
            control.markAsTouched();
          }

          this.updateCheckedProductsList();
          this.updateExistNotUpdatedProduct();

          this.ref.markForCheck();
        }));
    });
  }
  updateCheckedProductsList() {
    setTimeout(() => {
      this.checkedProducts = [];
      this.cartForm.value.products.map(product => {
        if (product && product.checked) {
          this.checkedProducts.push(product);
        }
      });
      this.checkedList.emit(this.checkedProducts);
    });
  }
  updateExistNotUpdatedProduct() {
    setTimeout(() => {
      this.existNotUpdatedProduct = this.setExistNotUpdatedProduct();
    });
  }


  decrementQuantity(currentQuantity, index, product: CartItem): void {
    if (+currentQuantity > 1) {
      this.cartFormProductsFA
        .at(index)
        .get<number>('quantity')
        .setValue(+currentQuantity - 1);
    }
    if ((+currentQuantity - product.increment) > 1) {
      if ((+currentQuantity - product.increment) % product.increment !== 0) {
        this.cartFormProductsFA
          .at(index)
          .get<number>('quantity')
          .setValue(+currentQuantity - product.increment - (+currentQuantity % product.increment));
      } else {
        this.cartFormProductsFA
          .at(index)
          .get<number>('quantity')
          .setValue(+currentQuantity - product.increment);
      }
    } else {
      this.cartFormProductsFA
        .at(index)
        .get<number>('quantity')
        .setValue(product.increment);
    }
  }

  incrementQuantity(currentQuantity: number, index: number, product: CartItem): void {
    if ((+currentQuantity + product.increment) % product.increment !== 0) {
      this.cartFormProductsFA
        .at(index)
        .get<number>('quantity')
        .setValue(+currentQuantity + product.increment - (+currentQuantity % product.increment));
    } else {
      this.cartFormProductsFA
        .at(index)
        .get<number>('quantity')
        .setValue(+currentQuantity + product.increment);
    }
  }

  setProducts(products: CartItem[]) {
    if (this.showFifoStocks) {
      this.products = this.cartHelperService.getProductsByBrandForCart(products);
    } else {
      this.products = this.cartHelperService.getProductsByCardForCart(products);
    }
  }

  getUserClientSelectorValue(id) {
    return getUserClientSelectorLabel(id, this.userClientRefObject);
  }

  updateProduct(cartItemFormItem: CartItemFormItem, index: number): void {
    if (this.cartFormProductsFA
      .at(index)
      .get<string>('note')
      .hasError('maxlength')) {
      return;
    }

    const product = this.products.find(item => item.id === cartItemFormItem.id);
    if (!product) {
      return;
    }

    const data = buildCartItemUpdate(product, cartItemFormItem, this.showFifoStocks);
    this.store.dispatch(new fromStore.UpdateProducts([{
      update: data,
      skn: cartItemFormItem.skn,
      product: null,
      matchId: 'cart-list',
    }]));
  }

  _keyPress(event: any): void {
    const pattern = /[0-9\+\-\ ]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  removeProduct(product: CartItem): void {
    this.store.dispatch(new fromCart.RemoveProduct(product));
    this.productRemoved.emit(true);
  }

  viewProduct(skn: string): void {
    this.router.navigate([`/search/${skn}`]);
  }

  setExistNotUpdatedProduct(): boolean {
    const hasUnsavedProducts = hasUnsavedCartProducts(this.products);
    this.existNotUpdatedProductChange.emit(hasUnsavedProducts);
    return hasUnsavedProducts;
  }

  saveAllProducts() {
    if (this.existNotUpdatedProduct) {
      const productsToUpdate = collectUnsavedProductUpdates(
        this.products,
        this.cartForm.value.products,
        this.showFifoStocks,
        'cart-list',
      );

      if (productsToUpdate.length <= 0) {
        this.existNotUpdatedProduct = false;
        this.existNotUpdatedProductChange.emit(false);
      } else {
        this.existNotUpdatedProduct = true;
        this.existNotUpdatedProductChange.emit(true);
        this.store.dispatch(
          new fromStore.UpdateProducts(
            productsToUpdate.map(ptu => ({
              update: ptu.update,
              skn: ptu.skn,
              product: null,
              matchId: 'cart-list',
            }))
          )
        );
      }
    }
  }

  validateQuantity(index: number, product: CartItem) {
    const normalizedQuantity = normalizeCartQuantity(
      this.cartFormProductsFA.at(index).get<number>('quantity').value,
      product.increment,
    );
    this.cartFormProductsFA
      .at(index)
      .get<number>('quantity')
      .setValue(normalizedQuantity);
  }
  ngOnDestroy(): void {
    if (this.updateProductsSuccessSub) {
      this.updateProductsSuccessSub.unsubscribe();
    }
    if (this.updateProductsFifoSuccessSub) {
      this.updateProductsFifoSuccessSub.unsubscribe();
    }
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
    if (this.removeStateSub) {
      this.removeStateSub.unsubscribe();
    }
    if (this.updateProductNotesSub) {
      this.updateProductNotesSub.unsubscribe();
    }
    if (this.cartValuesChangeSub) {
      this.cartValuesChangeSub.unsubscribe();
    }
    if (this.productsUpdatedEventObservableSub) {
      this.productsUpdatedEventObservableSub.unsubscribe();
    }
    for (const key in this.productFormSubs) {
      if (this.productFormSubs.hasOwnProperty(key)) {
        if (this.productFormSubs[key]) {
          this.productFormSubs[key].unsubscribe();
        }
      }
    }
  }

  private removeProductFormGroups() {
    clearFormArray(this.cartFormProductsFA);
  }

}
